import { createTheme } from '@mui/material/styles';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * MOTYW „SYGNAŁ" — przeniesiony z zaakceptowanego konceptu R5-15-009 (kierunek C).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Poprzedni motyw był „celowo skromny", żeby nie przeszkadzać przy czterdziestu wierszach liczb.
 * To była słuszna decyzja na etapie, w którym produkt dopiero dowodził, że w ogóle liczy dobrze.
 * Teraz zadanie jest inne: właściciel ma otworzyć Control Center i w ułamku sekundy wiedzieć,
 * co jest ważne. Neutralność nie odpowiada na to pytanie — hierarchia odpowiada.
 *
 * TOKENY SĄ W JEDNYM MIEJSCU: `src/styles/tokens.css`. Ten plik ich NIE duplikuje, tylko czyta
 * przez `var(--…)`. Dwa źródła prawdy o kolorze marki rozjeżdżają się w pierwszym tygodniu.
 *
 * WYJĄTEK: paleta MUI (`palette.primary.main` itd.) musi dostać wartości dosłowne, bo MUI liczy
 * z nich kontrast i warianty (`light`/`dark`) — a tego nie da się zrobić na `var()`. Te wartości
 * są zduplikowane ŚWIADOMIE i tylko tutaj; komentarz obok każdej mówi, z czego pochodzi.
 */

const ZLOTO = '#f7b10b'; // rodzina złota ze strony marketingowej TuSzamy
const ATRAMENT = '#191923'; // wypełnienie znaku firmowego (website/logo_black.svg)
const SUKCES = '#0f9b76';
const UWAGA = '#d9722e';
const KRYTYCZNY = '#d1343f';

export const theme = createTheme({
  palette: {
    mode: 'light',
    /*
     * Złoto jako `primary` jest decyzją, nie estetyką: w MUI `primary` koloruje wszystko, co
     * interaktywne. Skoro reguła języka brzmi „złoto = marka i interakcja", to jest dokładnie
     * to miejsce. Ostrzeżenie dostaje własną barwę (`warning`), żeby nigdy nie zlało się z marką.
     */
    primary: { main: ZLOTO, contrastText: ATRAMENT },
    secondary: { main: '#6c63c7' },
    success: { main: SUKCES },
    warning: { main: UWAGA },
    error: { main: KRYTYCZNY },
    background: { default: '#faf7f0', paper: '#ffffff' },
    text: { primary: ATRAMENT, secondary: '#4a4a5e' },
    divider: 'rgba(25, 25, 35, 0.1)',
  },

  typography: {
    /*
     * KRÓJ PODSTAWOWY CZYTAMY Z TOKENÓW, nie wpisujemy go tutaj drugi raz.
     *
     * Wcześniej stała tu własna lista („Segoe UI", …), a `--krój-ui` w tokenach był
     * nieużywany — dwie definicje tego samego, z których jedna nic nie robiła. Zmiana kroju
     * wymagała trafienia w tę właściwą; teraz jest jedno miejsce (`styles/tokens.css`).
     *
     * Wykresy rysujemy sami w SVG i one też sięgają po zmienną CSS — dzięki temu tekst
     * na wykresie i tekst w tabeli są tym samym krojem bez osobnego uzgadniania.
     */
    fontFamily: 'var(--krój-ui)',
    /*
     * Nagłówki idą WERSALIKAMI tylko na poziomie h4 (tytuł ekranu). Od 2026-10-04 wszystkie
     * tokeny kroju (`--krój-display`, `--krój-marka`, `--krój-ui`) wskazują ten sam
     * Plus Jakarta Sans — rangę budują stopień, waga i wersaliki, a nie osobna rodzina.
     * Waga 900 z dawnego kontraktu zostaje zapisana bez zmian; oś kroju kończy się na 800,
     * więc przeglądarka rysuje prawdziwe 800, bez syntezy (`font-synthesis: none`).
     */
    h4: {
      fontFamily: 'var(--krój-display)',
      fontWeight: 900,
      letterSpacing: '0.01em',
      textTransform: 'uppercase',
      lineHeight: 1.05,
    },
    /*
      h5 = TYTUŁ BLOKU TREŚCI, nie „mniejszy nagłówek ekranu".
      W makiecie odpowiada mu `.karta-tytul`: krój marki, 16 px, waga 900. Produkt renderował tu
      Segoe UI 24 px zapisem zdaniowym, czyli TEN SAM rejestr co tytuł ekranu — przez co tytuł
      bloku konkurował z tytułem ekranu zamiast się pod niego podporządkować. Audyt parytetu
      wypisał tę różnicę osobno przy siedmiu ekranach; to jedna przyczyna, nie siedem.
    */
    h5: {
      fontFamily: 'var(--krój-marka)',
      fontWeight: 900,
      fontSize: 'var(--st-sredni)',
      letterSpacing: '-0.01em',
    },
    h6: { fontWeight: 800, letterSpacing: '-0.01em' },
    overline: { fontWeight: 700, letterSpacing: '0.14em', fontSize: 11 },
  },

  shape: { borderRadius: 10 },

  components: {
    MuiTypography: {
      defaultProps: {
        /*
          RANGA ≠ STYL. `variant="h5"` niesie WYGLĄD tytułu bloku, ale w dokumencie taki blok
          stoi bezpośrednio pod tytułem ekranu (`h1`), więc jego rangą jest `h2`. Domyślne
          mapowanie MUI dawało `<h5>` — przeskok o trzy poziomy, który czytnik ekranu zgłasza
          jako lukę w strukturze. Ten sam błąd złapaliśmy wcześniej ręcznie na Jakości danych;
          tutaj przestaje być możliwy do popełnienia.
        */
        variantMapping: {
          h5: 'h2',
          h6: 'h3',
          /*
            `subtitle1`/`subtitle2` mapują się w MUI DOMYŚLNIE NA `<h6>` — i to jest cicha
            pułapka. Wariant „subtitle" bywa zwykłym tekstem prowadzącym, a nie nagłówkiem,
            więc produkt rozsiewał po ekranach `<h6>` bez żadnej intencji. Sprawdzian
            dostępności pokazał to jako przeskoki h2→h6 na czterech ekranach.

            Domyślnie NIE są więc nagłówkami. Tam, gdzie subtitle NAPRAWDĘ tytułuje blok,
            ranga jest podana jawnie przez `component` — świadomie, a nie przez przypadek.
          */
          subtitle1: 'p',
          subtitle2: 'p',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          whiteSpace: 'nowrap',
          fontSize: 11,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--tekst-slaby)',
        },
        // Bez pionowych linii i bez zebry — rytm buduje odstęp, nie kreska.
        root: { borderColor: 'var(--linia)' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 'var(--r-karta)' },
        /*
         * KARTA BEZ OBRAMOWANIA W SPOCZYNKU — reguła makiety, wcześniej tylko zadeklarowana.
         *
         * Ten komentarz stał tu od R5-15-010, ale nadpisanie ustawiało wyłącznie promień. Efekt:
         * 87 miejsc w 28 plikach renderowało `variant="outlined"`, czyli kreskę wokół każdego
         * bloku — dokładnie to, czego makieta unika i co wprost nazywa główną przyczyną, dla
         * której panele wyglądają jak arkusz kalkulacyjny. Właściciel zobaczył różnicę, której
         * przyczyną była jedna nieuzupełniona reguła.
         *
         * Poprawka idzie przez MOTYW, nie przez 87 edycji: `variant="outlined"` zostaje w kodzie
         * jako deklaracja „to jest karta", a wygląd rozstrzyga się w jednym miejscu. Wycofanie
         * całości to usunięcie tego bloku.
         */
        outlined: {
          border: 'none',
          boxShadow: 'var(--cien-1)',
          background: 'var(--powierzchnia)',
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 'var(--r-karta)',
          boxShadow: 'var(--cien-1)',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 'var(--r-pigulka)', fontWeight: 700, textTransform: 'none' },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Ognisko klawiatury musi być widoczne i musi być złote — to jedyny stan, w którym
        // marka dotyka interakcji użytkownika bezpośrednio.
        ':focus-visible': {
          outline: `3px solid ${ZLOTO}`,
          outlineOffset: 2,
        },
      },
    },
  },
});
